import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { UseMutationResult, useQuery } from '@tanstack/react-query';
import BasicModal from '__components/Modals/presets/BasicModal';
import { useMarketplacePermissions } from '__pages/Marketplace/permissions';
import { useTranslation } from 'react-i18next';
import PluginService from 'services/PluginService';
import { PLUGINS } from 'queries/query-keys';
import BasicTable from '__components/Tables/Basic';
import { SizedCell, SortableHeadCell, TRow } from '__components/Kizen/Table';
import { useMemo } from 'react';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import styled from '@emotion/styled';
import Loader from '__components/Kizen/Loader';
import { useSelector } from 'react-redux';
import { useBreakpoint } from '__app/spacing';

const CenteredLoader = styled(Loader)`
  position: absolute;
  top: 60%;
  left: 50%;
  padding-top: 20px !important;
  transform: translate(-50%, 0%);
`;

type ServiceProps = {
  service: any;
  oAuthEvents: Array<any>;
  logoutMutation: UseMutationResult<
    any,
    Error,
    { service_name: string; display_name: string }
  >;
  pendingRevoke: {
    display_name: string;
    service_name: string;
  } | null;
  setPendingRevoke: (
    value: {
      display_name: string;
      service_name: string;
    } | null
  ) => void;
  businessPluginApp: Record<string, any>;
  mode: 'business' | 'user';
  inline?: boolean;
};

export const Service = (props: ServiceProps) => {
  const {
    service,
    oAuthEvents,
    logoutMutation,
    pendingRevoke,
    setPendingRevoke,
    businessPluginApp,
    mode,
    inline = false,
  } = props;

  const { t } = useTranslation();

  const isUserMode = mode === 'user';

  const currentUser = useSelector((s: any) => s.authentication.teamMember);

  const serviceErrorMessages = useMemo(() => {
    const messages = oAuthEvents.filter(
      (e) =>
        e.event_type === 'error' && e.context?.service === service.service_name
    );

    return messages;
  }, [oAuthEvents, service.service_name]);

  const serviceInfoMessages = useMemo(() => {
    const messages = oAuthEvents.filter(
      (e) =>
        e.event_type === 'info' && e.context?.service === service.service_name
    );

    if (isUserMode) {
      return messages.filter((msg) =>
        msg.resource_path?.includes(`employee.${currentUser.id}`)
      );
    }

    return messages;
  }, [oAuthEvents, service.service_name, currentUser, isUserMode]);

  const hasErrors = serviceErrorMessages.length > 0;

  const { canEditPlugins } = useMarketplacePermissions(
    undefined,
    businessPluginApp
  );

  const canEditAuth = isUserMode || canEditPlugins;

  const showAuthorizedUsers = canEditPlugins && isUserMode;

  const isMobile = useBreakpoint();

  const {
    data: authorizedUsersData,
    isLoading: isLoadingAuthorizedUsers,
    refetch: refetchAuthorizedUsers,
  } = useQuery({
    queryFn: () =>
      PluginService.getAuthorizedUsers(
        businessPluginApp.id,
        service.service_name
      ),
    queryKey: PLUGINS.GET_AUTHORIZED_USERS(
      businessPluginApp.id,
      service.service_name
    ),
    enabled: showAuthorizedUsers,
    // Always refetch on focus because we need this state to be up to date when returning to the tab after authorizing
    refetchOnWindowFocus: 'always',
  });

  const authorizedUsers = useMemo(() => {
    const users = authorizedUsersData?.users || [];

    return users;
  }, [authorizedUsersData]);

  const tableColumns = useMemo(() => {
    return [
      {
        id: 'name',
        headCell: (
          <SortableHeadCell label={t('Display Name')} hasSorting={false} />
        ),
        cell: <SizedCell />,
        format: (_: any, data: any) => {
          return <Typography>{data.display_name}</Typography>;
        },
      },
      {
        id: 'status',
        headCell: <SortableHeadCell label={t('Status')} hasSorting={false} />,
        cell: <SizedCell />,
        format: (_: any, data: any) => {
          return (
            <div>
              <Typography color="viz-cat-1-primary">
                {t('Authorized')}
              </Typography>
            </div>
          );
        },
      },
    ];
  }, [t]);

  return (
    <>
      <div
        key={service.service_name}
        className={`pt-10 ${showAuthorizedUsers ? `gap-20 ${isMobile ? 'flex flex-col' : 'grid grid-cols-2'}` : ''}`}
      >
        <div>
          <Typography variant="header" weight="semibold" size="lg">
            {t('{{displayName}}', {
              displayName: service.display_name || t('App'),
            })}
          </Typography>

          {serviceErrorMessages.length > 0 ? (
            <>
              <Spacer size={5} />
              <div className="flex">
                <div className="flex flex-col">
                  {serviceErrorMessages.map((msg) => (
                    <div
                      key={msg.context.created}
                      className="bg-viz-seq-red-10 flex mt-4 p-[10px] rounded-[4px]"
                    >
                      <Typography>
                        {msg.context.error_message.replace(
                          service.service_name,
                          service.display_name
                        )}
                        {msg.context.reason ? ` (${msg.context.reason})` : ''}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
          {serviceInfoMessages.length && serviceErrorMessages.length ? (
            <Spacer size={15} />
          ) : null}
          {serviceInfoMessages.length > 0 ? (
            <>
              <Spacer size={5} />
              <div className="flex">
                <div className="flex flex-col">
                  {serviceInfoMessages.map((msg) => (
                    <div
                      key={msg.context.created}
                      className="bg-feedback-info-bg flex mt-4 p-[10px] rounded-[4px]"
                    >
                      <Typography>
                        {msg.context.message.replace(
                          service.service_name,
                          service.display_name
                        )}
                      </Typography>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}
          {canEditAuth ? <Spacer size={15} /> : null}

          <div className="flex gap-10">
            {canEditAuth ? (
              <Button
                onClick={() =>
                  window.open(
                    `${import.meta.env.VITE_API_BASE_PATH}/external-integrations/business-plugin-apps/${businessPluginApp.id}/services/${encodeURIComponent(
                      service.service_name
                    )}/authorize?success_redirect_path=${encodeURIComponent(
                      window.location.pathname
                    )}&error_redirect_path=${encodeURIComponent(
                      window.location.pathname
                    )}`,
                    '_blank'
                  )
                }
              >
                {hasErrors ? t('Authorize App') : t('Re-Authorize App')}
              </Button>
            ) : null}
            {hasErrors || !canEditAuth ? null : (
              <>
                <Button
                  variant="text"
                  color="warning"
                  onClick={() => setPendingRevoke(service)}
                >
                  {t('Revoke Authorization')}
                </Button>
              </>
            )}
          </div>
        </div>
        {showAuthorizedUsers && !inline ? (
          <div>
            <Typography variant="header" weight="semibold" size={'md'}>
              {t('Authorized Users')}
            </Typography>
            <Spacer size={20} />
            <Typography>
              {t(
                'The following users have authorized {{serviceName}} for their account:',
                {
                  serviceName: service.display_name || t('this service'),
                }
              )}
            </Typography>
            <Spacer size={20} />
            <div className="max-h-[300px] flex flex-col relative">
              <TableScrollContainer bottom>
                <BasicTable
                  stickyHeader
                  head={
                    <TRow head columns={tableColumns} data={authorizedUsers} />
                  }
                  staleData={isLoadingAuthorizedUsers}
                >
                  {authorizedUsers.map((u: any) => {
                    return <TRow key={u.id} columns={tableColumns} data={u} />;
                  })}
                </BasicTable>
              </TableScrollContainer>
              {isLoadingAuthorizedUsers ? <CenteredLoader loading /> : null}
            </div>
          </div>
        ) : null}
      </div>
      <BasicModal
        show={Boolean(pendingRevoke)}
        heading={t('Revoke Authorization')}
        buttonText={t('Confirm')}
        onConfirm={async () => {
          await logoutMutation.mutateAsync(pendingRevoke!);
          if (showAuthorizedUsers) {
            refetchAuthorizedUsers?.();
          }
        }}
        onHide={() => setPendingRevoke(null)}
      >
        <Typography>
          {t(
            'Are you sure you want to revoke authorization for {{displayName}}? If authorization is disconnected, you may need to re-authorize the app to continue using it.',
            {
              displayName: pendingRevoke?.display_name,
            }
          )}
        </Typography>
        <Spacer size={5} />
      </BasicModal>
    </>
  );
};
